import type { GroupOrder, GroupOrderStatus } from './schema'

type StatusBadgeTone =
  | 'unpaid'
  | 'billed'
  | 'neutral'
  | 'payment'
  | 'processing'
  | 'shipping-wait'
  | 'shipping-active'
  | 'success'
  | 'cancelled'

export interface OrderStatusBadgeView {
  label: string
  tone: StatusBadgeTone
}

const fallbackLabels: Record<GroupOrderStatus, string> = {
  UNPAID: 'Menunggu Konfirmasi',
  BILLED: 'Menunggu Pembayaran',
  IN_PROCESS: 'Diproses',
  SHIPPED: 'Dikirim',
  COMPLETED: 'Selesai',
  CANCELLED: 'Dibatalkan',
  UNKNOWN: 'Lainnya',
}

const toneClasses: Record<StatusBadgeTone, string> = {
  unpaid: 'bg-slate-800 text-white',
  billed: 'bg-[#808080] text-white',
  neutral: 'bg-slate-100 text-ink',
  payment: 'bg-sky-100 text-sky-800',
  processing: 'bg-[#E5FFFF] text-[#004D4D]',
  'shipping-wait': 'bg-[#FFF4DC] text-[#8B6914]',
  'shipping-active': 'bg-[#FFF4DC] text-[#8B6914]',
  success: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-600',
}

export function orderHasUnclearProduct(order: GroupOrder): boolean {
  return order.hasUnclearProduct ?? order.items.some((item) => item.isUnclear)
}

export function orderHasNeverBeenBilled(order: GroupOrder): boolean {
  return (
    order.status === 'UNPAID' &&
    order.billedCount === 0 &&
    !order.invoiceId &&
    !order.invoiceStatus
  )
}

/** Label + color tone for the status pill in the order table. */
export function resolveOrderStatusBadge(order: GroupOrder): OrderStatusBadgeView {
  if (order.status === 'BILLED') {
    if (order.invoiceStatus === 'PENDING') {
      return { label: 'Menunggu pembayaran', tone: 'payment' }
    }
    const times = order.billedCount > 0 ? ` ${order.billedCount}x` : ''
    return { label: `Ditagihkan${times}`, tone: 'billed' }
  }

  if (order.status === 'IN_PROCESS') {
    return { label: order.statusMessage || 'Diproses', tone: 'processing' }
  }

  if (order.status === 'SHIPPED') {
    return shippedStatusBadge(order.statusMessage)
  }

  if (order.status === 'COMPLETED') {
    return { label: fallbackLabels.COMPLETED, tone: 'success' }
  }

  if (order.status === 'CANCELLED') {
    return { label: fallbackLabels.CANCELLED, tone: 'cancelled' }
  }

  if (order.status === 'UNPAID') {
    return orderHasUnclearProduct(order)
      ? { label: fallbackLabels.UNPAID, tone: 'unpaid' }
      : {
          label: orderHasNeverBeenBilled(order) ? 'Belum ditagihkan' : 'Menunggu Pembayaran',
          tone: 'payment',
        }
  }

  return { label: fallbackLabels[order.status], tone: 'neutral' }
}

function shippedStatusBadge(statusMessage: string | null): OrderStatusBadgeView {
  const normalized = statusMessage?.trim().toLowerCase() ?? ''

  if (normalized.includes('sedang dikirim') || normalized === 'bkd') {
    return { label: 'Sedang dikirim', tone: 'shipping-active' }
  }

  if (
    normalized.includes('menunggu drop') ||
    normalized.includes('menunggu drop paket') ||
    normalized === 'drpcrt'
  ) {
    return { label: 'Menunggu drop paket', tone: 'shipping-wait' }
  }

  const label = statusMessage?.trim() || 'Menunggu drop paket'
  const tone: StatusBadgeTone = normalized.includes('sedang dikirim')
    ? 'shipping-active'
    : 'shipping-wait'

  return { label, tone }
}

export function orderStatusBadgeClass(tone: StatusBadgeTone) {
  return toneClasses[tone]
}

/** Shipment ID sits under the badge once a label exists (ready to ship / in transit / done, including cancelled). */
export function orderShowsShipmentId(order: GroupOrder) {
  return (
    Boolean(order.shipmentId || order.sttNo) &&
    (order.status === 'IN_PROCESS' ||
      order.status === 'SHIPPED' ||
      order.status === 'COMPLETED' ||
      order.status === 'CANCELLED')
  )
}

/** Skip duplicating status_message below the badge when it is already the pill label. */
export function orderStatusMessageInBadge(order: GroupOrder) {
  return order.status === 'IN_PROCESS' || order.status === 'SHIPPED'
}
